import React, { useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import Card from '../common/Card';
import { LoadingSpinner } from '../common/LoadingSpinner';
import { ShieldCheckIcon, ArrowLeftIcon } from '@heroicons/react/24/outline';

interface MFAChallengeProps {
  onBackToLogin?: () => void;
}

export const MFAChallenge: React.FC<MFAChallengeProps> = ({ onBackToLogin }) => {
  const [verificationCode, setVerificationCode] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  const { verifyMFA, error, clearError, logout } = useAuth();

  const handleVerifyMFA = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!verificationCode.trim() || verificationCode.length !== 6) {
      return;
    }

    try {
      setIsVerifying(true);
      clearError();
      await verifyMFA(verificationCode);
      // MFA verification successful - user will be automatically signed in
    } catch (error) {
      console.error('Failed to verify MFA:', error);
      setVerificationCode(''); // Clear the code on error
    } finally {
      setIsVerifying(false);
    }
  };

  const handleBackToLogin = async () => {
    if (onBackToLogin) {
      onBackToLogin();
    } else {
      await logout();
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            Two-Factor Authentication
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            Enter the 6-digit code from your authenticator app
          </p>
        </div>

        <Card className="mt-8 space-y-6">
          <div>
            {error && (
              <div className="rounded-md bg-red-50 p-4 mb-4">
                <div className="text-sm text-red-700">
                  <p>{error}</p>
                </div>
              </div>
            )}

            <div className="space-y-6">
              <div className="text-center">
                <div className="flex items-center justify-center mb-4">
                  <ShieldCheckIcon className="h-12 w-12 text-blue-500" />
                </div>
                
                <div className="bg-blue-50 border border-blue-200 rounded-md p-4 mb-6">
                  <p className="text-sm text-blue-800">
                    <strong>Security Check Required</strong>
                  </p>
                  <p className="text-sm text-blue-700 mt-1">
                    Open your authenticator app and enter the current 6-digit code for FinStack.
                  </p>
                </div>
              </div>

              <form onSubmit={handleVerifyMFA} className="space-y-4">
                <div>
                  <label htmlFor="mfa-code" className="block text-sm font-medium text-gray-700 text-center">
                    Verification Code
                  </label>
                  <input
                    id="mfa-code"
                    name="mfaCode"
                    type="text"
                    maxLength={6}
                    pattern="[0-9]{6}"
                    autoComplete="one-time-code"
                    required
                    className="mt-1 appearance-none relative block w-full px-3 py-3 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-lg text-center font-mono tracking-widest"
                    placeholder="000000"
                    value={verificationCode}
                    onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    disabled={isVerifying}
                    autoFocus
                  />
                  <p className="text-xs text-gray-500 text-center mt-1">
                    Enter the 6-digit code from your authenticator app
                  </p>
                </div>

                <div className="space-y-3">
                  <button
                    type="submit"
                    disabled={isVerifying || verificationCode.length !== 6}
                    className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:bg-blue-400 disabled:cursor-not-allowed"
                  >
                    {isVerifying ? (
                      <LoadingSpinner size="small" color="white" />
                    ) : (
                      'Verify and Continue'
                    )}
                  </button>

                  <button
                    type="button"
                    onClick={handleBackToLogin}
                    className="w-full flex justify-center items-center py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    <ArrowLeftIcon className="h-4 w-4 mr-2" />
                    Back to Sign In
                  </button>
                </div>
              </form>

              <div className="bg-gray-50 border border-gray-200 rounded-md p-4">
                <h4 className="text-sm font-medium text-gray-900 mb-2">
                  Trouble with your authenticator app?
                </h4>
                <ul className="text-sm text-gray-700 space-y-1">
                  <li>• Make sure your device's time is synced correctly</li>
                  <li>• Try refreshing the code in your authenticator app</li>
                  <li>• Check that you're using the code for "FinStack"</li>
                </ul>
              </div>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};